"use client"

import Image from "next/image"
import { useState } from "react"
import { ArrowRight, FileText, GitBranch, MessageSquareText, Target } from "lucide-react"

const panels = [
  {
    key: "diagnose",
    label: "Discover",
    title: "Client interviews reveal the language buyers already trust.",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=90",
    alt: "Consultants running a stakeholder interview workshop",
    evidence: "Client themes, stakeholder map, trust blockers, service expectations, and next-best questions.",
    icon: MessageSquareText,
  },
  {
    key: "model",
    label: "Frame",
    title: "Strategic options become practical thresholds.",
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=90",
    alt: "Business analysis documents and strategy memo",
    evidence: "Option logic, tradeoff economics, reversible moves, priority filters, and no-go triggers.",
    icon: Target,
  },
  {
    key: "install",
    label: "Install",
    title: "The recommendation becomes a management rhythm.",
    image: "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1200&q=90",
    alt: "Consulting team reviewing operating plan on a wall",
    evidence: "Owner ritual, weekly room, escalation protocol, scorecard, and stop condition.",
    icon: GitBranch,
  },
  {
    key: "brief",
    label: "Brief",
    title: "The advisory memo keeps working after the workshop ends.",
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=90",
    alt: "Executive team reviewing a consulting brief on a laptop",
    evidence: "Narrative spine, tradeoffs, objections, recommended path, and decision receipt.",
    icon: FileText,
  },
]

export function DecisionRevealLab() {
  const [activeKey, setActiveKey] = useState(panels[0].key)
  const activePanel = panels.find((panel) => panel.key === activeKey) ?? panels[0]
  const ActiveIcon = activePanel.icon

  return (
    <section id="lab" className="relative overflow-hidden bg-primary px-5 py-24 text-primary-foreground sm:px-8 lg:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_22%,rgba(75,213,255,0.14),transparent_32%),radial-gradient(circle_at_80%_56%,rgba(219,255,74,0.12),transparent_34%)]" />
      <div className="relative mx-auto grid w-full max-w-[94rem] gap-8 lg:grid-cols-[22rem_1fr]">
        <div>
          <p className="font-mono text-xs font-black uppercase tracking-[0.24em] text-accent">Interactive lab</p>
          <h2 className="mt-5 font-serif text-5xl font-black leading-none sm:text-7xl">
            Click through the advisory artifacts.
          </h2>
          <p className="mt-6 leading-8 text-white/64">
            Each tab reveals a practical output, so the site feels like a working advisory room instead of a static services brochure.
          </p>
          <div className="mt-8 grid gap-2">
            {panels.map((panel) => (
              <button
                key={panel.key}
                type="button"
                aria-pressed={activeKey === panel.key}
                onClick={() => setActiveKey(panel.key)}
                className={`group flex items-center justify-between rounded-full border px-5 py-4 text-left font-mono text-xs font-black uppercase tracking-[0.18em] transition duration-300 ${
                  activeKey === panel.key
                    ? "border-accent bg-accent text-accent-foreground"
                    : "border-white/14 bg-white/[0.04] text-white/70 hover:border-secondary hover:bg-secondary/10 hover:text-white"
                }`}
              >
                {panel.label}
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
              </button>
            ))}
          </div>
        </div>

        <article className="reveal-lab-panel grid overflow-hidden rounded-[2rem] border border-white/14 bg-white/[0.06] backdrop-blur lg:grid-cols-[1.08fr_0.92fr]">
          <div className="relative min-h-[23rem] overflow-hidden">
            <Image
              key={activePanel.image}
              src={activePanel.image}
              alt={activePanel.alt}
              fill
              sizes="(min-width: 1024px) 48vw, 100vw"
              className="reveal-lab-image object-cover"
            />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent,rgba(16,17,15,0.84))]" />
            <p className="absolute bottom-5 left-5 rounded-full bg-primary/75 px-4 py-2 font-mono text-[0.66rem] font-black uppercase tracking-[0.18em] text-accent backdrop-blur">
              {activePanel.label} artifact
            </p>
          </div>
          <div className="p-6 sm:p-8">
            <div className="grid size-14 place-items-center rounded-full bg-accent text-accent-foreground">
              <ActiveIcon className="size-6" />
            </div>
            <h3 className="mt-10 font-serif text-4xl font-black leading-none sm:text-5xl">
              {activePanel.title}
            </h3>
            <p className="mt-6 leading-8 text-white/68">{activePanel.evidence}</p>
            <div className="mt-10 grid gap-3">
              {["Owner", "Evidence", "Decision", "Rhythm"].map((item) => (
                <div key={item} className="flex items-center justify-between border-t border-white/14 pt-3">
                  <span className="font-mono text-[0.66rem] font-black uppercase tracking-[0.18em] text-white/48">{item}</span>
                  <span className="h-2 w-24 rounded-full bg-accent" />
                </div>
              ))}
            </div>
          </div>
        </article>
      </div>
    </section>
  )
}
